// src/pages/verification.jsx — FIXED
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button, OtpInput, Alert } from '../components/ui';

export default function VerifyPage() {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const { verifyOtp, verifyEmail, resendCode, pendingVerification, isLoading } = useAuth();
  const navigate = useNavigate();

  const isEmail = !!pendingVerification?.email;
  const identifier = pendingVerification?.identifier || pendingVerification?.email || pendingVerification?.phone;

  // Agar pendingVerification yo'q bo'lsa, loginга redirect
  useEffect(() => {
    if (!pendingVerification) {
      navigate('/login', { replace: true });
    }
  }, [pendingVerification, navigate]);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) { setCanResend(true); return; }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (code.length < 6) { setError(t('apiErrors.AUTH_OTP_ERROR')); return; }
    setError('');
    try {
      let user;
      if (isEmail) {
        user = await verifyEmail(pendingVerification.email, code);
      } else {
        const requestId = pendingVerification?.requestId || pendingVerification?.id;
        if (!requestId) {
          setError("Verifikatsiya ma'lumotlari yo'q. Qayta kirish qiling.");
          return;
        }
        user = await verifyOtp(requestId, code);
      }

      if (user.role === 'admin') navigate('/dashboard', { replace: true });
      else if (user.role === 'driver') navigate('/driver/dashboard', { replace: true });
      else navigate('/main', { replace: true });
    } catch (err) {
      setError(t('apiErrors.AUTH_OTP_ERROR'));
      setCode('');
    }
  };

  const handleResend = async () => {
    setCode('');
    setError('');
    setSuccess('');
    try {
      await resendCode();
      setCountdown(60);
      setCanResend(false);
      setSuccess(isEmail ? 'Yangi kod emailingizga yuborildi.' : 'Yangi OTP telefon raqamingizga yuborildi.');
    } catch (err) {
      setError('Qayta yuborishda xatolik. Bir ozdan keyin urinib ko\'ring.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/40 dark:from-[#080f0b] dark:via-[#0c1510] dark:to-[#080f0b] flex items-center justify-center px-4 py-8">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 -right-32 h-[500px] w-[500px] rounded-full bg-[#1e9f6b]/10 blur-[80px]" />
        <div className="absolute -bottom-32 -left-32 h-[400px] w-[400px] rounded-full bg-sky-400/8 blur-[80px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm relative z-10"
      >
        <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-slate-100 dark:border-slate-800 shadow-2xl p-6 sm:p-8">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 300 }}
              className="h-20 w-20 bg-[#1e9f6b]/10 rounded-3xl flex items-center justify-center mx-auto mb-5"
            >
              <span className="material-symbols-outlined text-[#1e9f6b] text-[40px]">
                {isEmail ? 'mail' : 'sms'}
              </span>
            </motion.div>
            <h1 className="text-2xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight">
              {isEmail ? (t('auth.emailCode') || 'Email tasdiqlash') : (t('auth.smsCode') || 'SMS tasdiqlash')}
            </h1>
            {identifier && (
              <p className="text-slate-500 font-bold italic uppercase text-[10px] tracking-widest mt-2 px-2">
                {identifier}
              </p>
            )}
            <p className="text-slate-400 text-xs mt-1">
              {isEmail ? 'Email manzilingizga 6 xonali kod yuborildi' : 'Telefon raqamingizga 6 xonali kod yuborildi'}
            </p>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-7">
            <OtpInput value={code} onChange={setCode} length={6} error={error ? ' ' : ''} />

            {error && (
              <Alert message={error} type="error" />
            )}
            {success && (
              <div className="text-center text-sm text-[#1e9f6b] font-semibold">{success}</div>
            )}

            <Button
              type="submit"
              size="xl"
              loading={isLoading}
              disabled={code.length < 6 || isLoading}
              className="h-14 font-black uppercase tracking-widest text-sm shadow-xl shadow-[#1e9f6b]/30"
            >
              {t('auth.submit') || 'Tasdiqlash'}
            </Button>

            <div className="text-center">
              {canResend ? (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isLoading}
                  className="text-[#1e9f6b] font-black uppercase tracking-widest text-[10px] hover:underline transition-all disabled:opacity-50"
                >
                  {t('auth.resendCode') || 'Qayta yuborish'}
                </button>
              ) : (
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                  {t('auth.resendTimer') || 'Qayta yuborish'}:{' '}
                  <span className="text-[#1e9f6b] italic">{countdown}s</span>
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-center text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors"
            >
              ← {t('auth.login') || 'Kirishga qaytish'}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
